import { CheckCircle2, MapPin, Mail, Phone } from "lucide-react";
import { OWNER, COUNTERS } from "@/data/portfolio";
import { useReveal, useCountUp } from "@/hooks/useReveal";

function Counter({ value, suffix, label, start }) {
  const n = useCountUp(value, 1600, start);
  return (
    <div className="text-center md:text-left">
      <div className="font-display font-bold text-4xl md:text-5xl text-white tracking-tight">
        {n}<span className="text-[#FF3B30]">{suffix}</span>
      </div>
      <p className="text-xs md:text-sm text-white/50 uppercase tracking-widest mt-2">{label}</p>
    </div>
  );
}

const highlights = [
  "Website Design",
  "Responsive Design",
  "UI/UX",
  "Frontend Development",
  "Clean Code",
  "SEO-friendly websites",
];

export default function About() {
  const { ref, visible } = useReveal();

  return (
    <section id="about" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" ref={ref}>
          <div className={`lg:col-span-5 fade-in-up ${visible ? "in" : ""}`}>
            <div className="section-label mb-5">About Me</div>
            <h2 className="heading-hero text-4xl md:text-5xl lg:text-6xl mb-6" data-testid="about-heading">
              Websites that <span className="text-[#FF3B30]">work</span> —<br/>as hard as you do.
            </h2>

            <p className="text-white/70 leading-relaxed text-base md:text-lg mb-6" data-testid="about-body-1">
              I'm <span className="text-white font-semibold">{OWNER.name}</span>, a Website Designer & Frontend Developer based in Chikmagalur, Karnataka. Over the last few years I have designed and shipped websites for homestays, hotels, coffee shops, schools, colleges, e-commerce brands, and local businesses across India.
            </p>
            <p className="text-white/60 leading-relaxed text-base mb-8" data-testid="about-body-2">
              My focus is on clean code, mobile-first responsive layouts, thoughtful UI/UX and SEO-friendly foundations so your website not only looks premium — it also gets found, loads fast, and converts.
            </p>

            <div className="grid grid-cols-2 gap-3 mb-8">
              {highlights.map((h) => (
                <div key={h} className="flex items-center gap-2 text-sm text-white/80">
                  <CheckCircle2 size={16} className="text-[#FF3B30]" /> {h}
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-2 text-sm text-white/70">
              <div className="flex items-center gap-3"><MapPin size={14} className="text-[#FF3B30]" /> {OWNER.location}</div>
              <div className="flex items-center gap-3"><Mail size={14} className="text-[#FF3B30]" /> {OWNER.email}</div>
              <div className="flex items-center gap-3"><Phone size={14} className="text-[#FF3B30]" /> {OWNER.phoneDisplay}</div>
            </div>
          </div>

          <div className={`lg:col-span-7 grid grid-cols-2 gap-4 md:gap-6 fade-in-up ${visible ? "in" : ""}`} style={{ transitionDelay: '150ms' }}>
            <div className="glass-card rounded-3xl p-6 md:p-8 col-span-2 md:col-span-1 md:row-span-2">
              <div className="section-label mb-4">What I do</div>
              <h3 className="font-display font-bold text-2xl mb-3 leading-tight">
                Design + build websites that feel expensive.
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">
                From the first Figma frame to the final deploy — I take care of design, development, SEO basics, and hand-off. You get a website ready to grow with your business.
              </p>
            </div>

            <div className="glass-card rounded-3xl p-6 md:p-8">
              <Counter value={COUNTERS[0].value} suffix={COUNTERS[0].suffix} label={COUNTERS[0].label} start={visible} />
            </div>
            <div className="glass-card rounded-3xl p-6 md:p-8">
              <Counter value={COUNTERS[1].value} suffix={COUNTERS[1].suffix} label={COUNTERS[1].label} start={visible} />
            </div>
            <div className="glass-card rounded-3xl p-6 md:p-8">
              <Counter value={COUNTERS[2].value} suffix={COUNTERS[2].suffix} label={COUNTERS[2].label} start={visible} />
            </div>
            <div className="glass-card rounded-3xl p-6 md:p-8">
              <Counter value={COUNTERS[3].value} suffix={COUNTERS[3].suffix} label={COUNTERS[3].label} start={visible} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
